import { useIntl } from 'react-intl';
import { navigate } from 'vike/client/router';
import { getIndexName } from '../constants';
import {
  generateUrlFromIndexUiState,
  isCompanyProfilePath,
  pathnameIsJobsListingPath,
  type IndexUiState,
} from '../utils/search-router/url-handling';
import { useSearchQuery, useSearchFilters, useSearchPage, useSearchSort } from '../contexts/SearchContext';
import { useNavigation } from '../contexts/NavigationContext';

/**
 * Hook to push a new indexUiState to the router and update local state, or just generate a URL for a given state.
 * Usage:
 *   const { pushIndexUiState, getUrlForIndexUiState } = usePushIndexUiState();
 *   const url = getUrlForIndexUiState(newUiState); // for render
 *   pushIndexUiState(newUiState); // for click handlers
 */
export function usePushIndexUiState() {
  const intl = useIntl();
  const memoIndexName = getIndexName(intl.locale);
  const { setQuery } = useSearchQuery();
  const { setFilters } = useSearchFilters();
  const { setPage } = useSearchPage();
  const { setSort } = useSearchSort();
  
  // Try to get navigation context for immediate feedback
  let navigation: { startNavigation: () => void; completeNavigation: () => void } | null = null;
  try {
    navigation = useNavigation();
  } catch {
    // Navigation context not available, continue without it
  }

  function getUrlForIndexUiState(newUiState: IndexUiState, currentPath?: string): string {
    const path = currentPath ?? (typeof window !== 'undefined' ? window.location.pathname : '');
    return generateUrlFromIndexUiState(newUiState, memoIndexName, path, {
      forceJobsListingForCompany: pathnameIsJobsListingPath(path),
    });
  }

  function pushIndexUiState(newUiState: IndexUiState): string {
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : undefined;
    const target =
      getUrlForIndexUiState(newUiState, currentPath) ||
      (typeof window !== 'undefined' ? window.location.pathname : '');
    const pathOnly = target.split('?')[0] || '';

    if (typeof window !== 'undefined' && isCompanyProfilePath(pathOnly)) {
      if (navigation) {
        navigation.startNavigation();
        setTimeout(() => {
          navigation?.completeNavigation();
        }, 400);
      }
      void navigate(target);
      return target;
    }

    // Show immediate feedback for search state changes
    if (navigation) {
      navigation.startNavigation();
      setTimeout(() => {
        navigation?.completeNavigation();
      }, 400);
    }

    // Optimistic UI updates - update state immediately
    if (typeof newUiState.query === 'string') {
      setQuery(newUiState.query);
    }
    if (typeof (newUiState as any).refinementList === 'object') {
      setFilters((newUiState as any).refinementList || {});
    }
    if (typeof newUiState.page === 'number') {
      const pageNum = Math.max(0, newUiState.page);
      setPage(pageNum);
    }
    if (typeof (newUiState as any).sort === 'string') {
      const s = (newUiState as any).sort as 'relevance' | 'date' | 'salary';
      if (s === 'relevance' || s === 'date' || s === 'salary') setSort(s);
    }

    window.history.pushState({}, '', target);
    try {
      setTimeout(() => window.dispatchEvent(new Event('urlchange')), 0);
    } catch {}
    return target;
  }

  return { pushIndexUiState, getUrlForIndexUiState };
}
